import { Star } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { collection, getDocs, query, where } from "firebase/firestore"
import { db } from "@/lib/firebase"

interface RatingSummaryProps {
  artistId: string
  className?: string
}

export function RatingSummary({ artistId, className }: RatingSummaryProps) {
  const [rating, setRating] = useState(0)
  const [reviewCount, setReviewCount] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let isMounted = true

    const fetchReviews = async () => {
      try {
        setIsLoading(true)
        const reviewsQuery = query(
          collection(db, "reviews"),
          where("artistId", "==", artistId)
        )
        const snapshot = await getDocs(reviewsQuery)
        let sum = 0
        let count = 0
        snapshot.docs.forEach(doc => {
          const data = doc.data() as { starRating?: number; isApproved?: boolean }
          if (data.isApproved !== true) return
          if (typeof data.starRating === "number") {
            sum += data.starRating
            count += 1
          }
        })

        if (!isMounted) return
        setReviewCount(count)
        setRating(count > 0 ? sum / count : 0)
      } catch (error) {
        console.error("[RatingSummary] Failed to load reviews:", error)
        if (!isMounted) return
        setReviewCount(0)
        setRating(0)
      } finally {
        if (!isMounted) return
        setIsLoading(false)
      }
    }

    fetchReviews()

    return () => {
      isMounted = false
    }
  }, [artistId])

  const roundedRating = useMemo(() => Math.round(rating * 10) / 10, [rating])
  const filledStars = useMemo(() => Math.round(rating), [rating])

  if (isLoading) {
    return null
  }

  if (reviewCount === 0) {
    return null
  }

  return (
    <div className={`flex items-center gap-2 text-sm text-muted-foreground animate-in fade-in duration-500 ${className || ""}`}>
      <span className="font-medium text-foreground">{roundedRating.toFixed(1)}</span>
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-3.5 h-3.5 ${
              star <= filledStars ? "fill-[#FFA500] text-[#FFA500]" : "text-muted-foreground"
            }`}
          />
        ))}
      </div>
      <span>({reviewCount})</span>
    </div>
  )
}
